import { useState, useEffect } from "react";
import { MapPin, Phone, Mail, MessageCircle } from "lucide-react";
import api, { formatApiError } from "../lib/api";
import SEO from "../components/SEO";
import { useTheme } from "../context/ThemeContext";
import { Reveal } from "../components/motion/Reveal";
import { Input } from "../components/ui/input";
import { Textarea } from "../components/ui/textarea";
import { toast } from "sonner";

export default function Contact() {
  const { content, seo } = useTheme();
  const [settings, setSettings] = useState(null);
  const [form, setForm] = useState({ name: "", email: "", phone: "", message: "" });
  const [loading, setLoading] = useState(false);
  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  useEffect(() => {
    api.get("/settings").then((r) => setSettings(r.data)).catch(() => {});
  }, []);

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await api.post("/enquiries", { ...form, type: "contact" });
      toast.success("Message sent! We will get back to you soon.");
      setForm({ name: "", email: "", phone: "", message: "" });
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="max-w-7xl mx-auto px-4 md:px-8 py-16" data-testid="contact-page">
      <SEO title={seo.contact_title || "Contact Us"} description={seo.contact_description || "Get in touch with Humaira Crafts — premium metal home décor manufacturer in Delhi."} />
      <Reveal>
        <h1 className="text-4xl md:text-5xl tracking-tighter font-semibold mb-16" data-eid="contact-heading" data-etype="text">{content.contact_heading}</h1>
      </Reveal>

      <div className="grid md:grid-cols-2 gap-16">
        <div className="flex flex-col gap-8">
          <div className="flex gap-4"><MapPin strokeWidth={1.5} size={22} className="text-gold shrink-0" /><div><p className="font-serif text-xl">Address</p><p className="text-sm text-muted-foreground mt-1" data-testid="contact-address" data-eid="contact-address" data-etype="text">{settings?.address || "Delhi, India"}</p></div></div>
          <div className="flex gap-4"><Phone strokeWidth={1.5} size={22} className="text-gold shrink-0" /><div><p className="font-serif text-xl">Phone</p><p className="text-sm text-muted-foreground mt-1" data-testid="contact-phone" data-eid="contact-phone" data-etype="text">{settings?.phone || "—"}</p></div></div>
          <div className="flex gap-4"><MessageCircle strokeWidth={1.5} size={22} className="text-gold shrink-0" /><div><p className="font-serif text-xl">WhatsApp</p><p className="text-sm text-muted-foreground mt-1" data-testid="contact-whatsapp" data-eid="contact-whatsapp" data-etype="text">{settings?.whatsapp || "—"}</p></div></div>
          <div className="flex gap-4"><Mail strokeWidth={1.5} size={22} className="text-gold shrink-0" /><div><p className="font-serif text-xl">Email</p><p className="text-sm text-muted-foreground mt-1" data-testid="contact-email" data-eid="contact-email" data-etype="text">{settings?.email || "—"}</p></div></div>
        </div>

        <div className="bg-white border border-black/10 p-8 md:p-10 self-start">
          <p className="small-caps text-gold mb-6">Send a Message</p>
          <form onSubmit={submit} className="flex flex-col gap-4">
            <Input required placeholder="Your Name *" value={form.name} onChange={set("name")} data-testid="contact-form-name" />
            <div className="grid grid-cols-2 gap-4">
              <Input required type="email" placeholder="Email *" value={form.email} onChange={set("email")} data-testid="contact-form-email" />
              <Input required placeholder="Phone *" value={form.phone} onChange={set("phone")} data-testid="contact-form-phone" />
            </div>
            <Textarea required placeholder="Your message *" rows={5} value={form.message} onChange={set("message")} data-testid="contact-form-message" />
            <button type="submit" disabled={loading} className="bg-[#111] text-[#FAF9F6] py-4 small-caps hover:bg-gold hover:text-[#111] transition-colors duration-300 disabled:opacity-50" data-testid="contact-form-submit">
              {loading ? "Sending..." : "Send Message"}
            </button>
          </form>
        </div>
      </div>
    </div>
  );
}
